import type { Alert, AlertPage } from '@fruuit/shared'
import { api } from '../../api/client'
import { type ApiQueryOptions, useApiMutation, useApiQuery } from '../../api/hooks'

export interface AlertFilters {
  status: 'open' | 'acknowledged' | 'all'
  severity?: string
  kind?: string
  provider?: string
}

export const alertKeys = {
  all: ['alerts'] as const,
  list: (filters: AlertFilters, cursor: string | null, limit: number) =>
    ['alerts', 'list', filters, cursor, limit] as const,
}

/** `GET /alerts` with filters; `counts` covers every open alert. */
export function useAlerts(
  filters: AlertFilters,
  cursor: string | null = null,
  limit = 25,
  options: Omit<ApiQueryOptions<AlertPage>, 'query'> = {},
) {
  return useApiQuery<AlertPage>(alertKeys.list(filters, cursor, limit), '/alerts', {
    query: {
      status: filters.status,
      severity: filters.severity || undefined,
      kind: filters.kind || undefined,
      provider: filters.provider || undefined,
      limit,
      cursor: cursor ?? undefined,
    },
    placeholderData: (previous) => previous,
    ...options,
  })
}

/** `POST /alerts/:id/acknowledge` with the alert's revision (CAS). */
export function useAcknowledgeAlert() {
  return useApiMutation(
    (alert: Pick<Alert, 'id' | 'revision'>, { requestId }) =>
      api.post<Alert>(
        `/alerts/${encodeURIComponent(alert.id)}/acknowledge`,
        { revision: alert.revision },
        { requestId },
      ),
    { invalidate: [alertKeys.all, ['feeds'], ['admin', 'overview']] },
  )
}
